import type { RoofPitchData, RoofProductionInformation } from '../../../../../@types/types';
import { resolveEffectiveAnnualUsageKwh } from '../../../../../helpers/utilityMonthlyDailyToAnnual';
import { getOpportunityPrimaryFinancePartner } from '../../oneBUTTONDashboard/contactCreditStatus';
import {
    evaluateFinanceEligibility,
    fetchFinanceEligibilityCriteria,
    fetchFinanceEligibilityPropertyDetails,
    findFinanceEligibilityPartner,
    getFailedSystemSpecReasons,
    isFinanceEligibilityValuePartner,
    INSTALLATION_TYPE_ROWS,
    type EligibilityCurrentValues,
} from '../../../../../services/financeEligibilityService';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../../store/store';

type EligibilityFace = {
    carport?: boolean;
    surfaceMount?: boolean;
    flatRoof?: boolean;
    plane?: boolean;
    buildingName?: number | string;
    roofFaceId?: string;
    roof_tag_id?: number;
    nameOverride?: number | string;
    moduleArr?: unknown[];
    pitchOverride?: number;
    oriAng?: { tilt?: number };
};

type EligibilityRoofMesh = {
    roofFaceId?: string;
    moduleArr?: unknown[];
    userData?: {
        pitchOverride?: number;
        nameOverride?: number | string;
        roof_tag_id?: number | string;
    };
    oriAng?: { tilt?: number };
};

type EligibilityTiltRoof = { name: string; tilt: number };

function formatEligibilityRoofName(
    ...parts: Array<string | number | null | undefined>
): string {
    for (const part of parts) {
        if (part == null || part === '') continue;
        const text = String(part).trim();
        if (!text) continue;
        return /^roof\s/i.test(text) ? text : `Roof ${text}`;
    }
    return '';
}

function pushEligibilityTilt(
    pitches: number[],
    roofs: EligibilityTiltRoof[],
    pitch: number,
    name: string,
): void {
    if (!Number.isFinite(pitch)) return;
    pitches.push(pitch);
    if (name) roofs.push({ name, tilt: pitch });
}

type EligibilitySceneViewer = {
    moduleMeshArr?: Array<{ userData?: { isBallastedRackType?: boolean } }>;
    roofMeshArr?: EligibilityRoofMesh[];
    roofs?: Array<{ faces?: EligibilityFace[] }>;
};

function roofHasPanels(
    info: RoofProductionInformation | undefined,
    moduleCount: number | undefined,
): boolean {
    const fromInfo = Number(info?.modules);
    if (Number.isFinite(fromInfo)) return fromInfo > 0;
    return Number(moduleCount ?? 0) > 0;
}

const INSTALLATION_LABELS: Record<string, string> = {
    roofMount: 'Roof Mount',
    flatRoofTiltKit: 'Flat Roof',
    groundMount: 'Ground Mount',
    carport: 'Carport',
    detachedStructure: 'Detached Structure',
};

export function getEligibilityTiltExtents(
    sceneViewer: EligibilitySceneViewer,
    roofInfoArr: RoofProductionInformation[] | undefined,
    roofPitchOverrideArr: RoofPitchData[] | undefined,
): { range: string; min: number | null; max: number | null; roofs: EligibilityTiltRoof[] } {
    const roofInfoByFaceId = new Map((roofInfoArr ?? []).map((roof) => [roof.roofFaceId, roof]));
    const pitches: number[] = [];
    const roofs: EligibilityTiltRoof[] = [];

    const roofMeshes = sceneViewer?.roofMeshArr;
    if (roofMeshes?.length) {
        for (const mesh of roofMeshes) {
            const info = mesh.roofFaceId ? roofInfoByFaceId.get(mesh.roofFaceId) : undefined;
            if (!roofHasPanels(info, mesh.moduleArr?.length)) continue;
            const pitch = info
                ? Math.round(Number(info.pitchOverride ?? info.pitch))
                : Math.round(Number(mesh.userData?.pitchOverride ?? mesh.oriAng?.tilt ?? 0));
            pushEligibilityTilt(
                pitches,
                roofs,
                pitch,
                formatEligibilityRoofName(
                    info?.nameOverride,
                    info?.roofNum,
                    mesh.userData?.nameOverride,
                    mesh.userData?.roof_tag_id,
                ),
            );
        }
    } else {
        const faces = sceneViewer?.roofs?.[0]?.faces ?? [];
        if (faces.length) {
            for (const face of faces) {
                const faceId = String(face.roofFaceId ?? face.roof_tag_id ?? '');
                const info = faceId ? roofInfoByFaceId.get(faceId) : undefined;
                const faceModuleCount = Array.isArray(face.moduleArr) ? face.moduleArr.length : 0;
                if (!roofHasPanels(info, faceModuleCount)) continue;
                const pitch = Math.round(Number(face.pitchOverride ?? face.oriAng?.tilt ?? 0));
                pushEligibilityTilt(
                    pitches,
                    roofs,
                    pitch,
                    formatEligibilityRoofName(
                        info?.nameOverride,
                        info?.roofNum,
                        face.nameOverride,
                        face.roof_tag_id,
                    ),
                );
            }
        } else {
            const panelRoofs = (roofInfoArr ?? []).filter((roof) => Number(roof.modules) > 0);
            const panelRoofIds = new Set(
                panelRoofs.map((roof) => String(roof.roofFaceId ?? roof.roofNum ?? '')),
            );
            const roofInfoById = new Map(
                panelRoofs.flatMap((roof) => {
                    const keys = [roof.roofFaceId, roof.roofNum]
                        .filter((key) => key != null && String(key) !== '')
                        .map((key) => [String(key), roof] as const);
                    return keys;
                }),
            );
            for (const entry of roofPitchOverrideArr ?? []) {
                if (panelRoofIds.size > 0 && !panelRoofIds.has(String(entry.roofId ?? ''))) continue;
                if (panelRoofIds.size === 0) continue;
                const pitch = Math.round(Number(entry.pitch));
                const info = roofInfoById.get(String(entry.roofId ?? ''));
                pushEligibilityTilt(
                    pitches,
                    roofs,
                    pitch,
                    formatEligibilityRoofName(info?.nameOverride, info?.roofNum, entry.roofId),
                );
            }
        }
    }

    if (pitches.length === 0) return { range: '', min: null, max: null, roofs: [] };
    const min = Math.min(...pitches);
    const max = Math.max(...pitches);
    return { range: `${min}-${max}`, min, max, roofs };
}

export function getEligibilityTiltRange(
    sceneViewer: EligibilitySceneViewer,
    roofInfoArr: RoofProductionInformation[] | undefined,
    roofPitchOverrideArr: RoofPitchData[] | undefined,
): string {
    return getEligibilityTiltExtents(sceneViewer, roofInfoArr, roofPitchOverrideArr).range;
}

export function getSmallestArraySize(roofInfoArr: RoofProductionInformation[] | undefined): string {
    const count = getSmallestArrayCount(roofInfoArr);
    return count == null ? '' : String(count);
}

export function getSmallestArrayCount(roofInfoArr: RoofProductionInformation[] | undefined): number | null {
    const counts = (roofInfoArr ?? [])
        .map((roof) => Number(roof.modules))
        .filter((count) => Number.isFinite(count) && count > 0);
    if (counts.length === 0) return null;
    return Math.min(...counts);
}

export function designHasBallastedRacking(sceneViewer: EligibilitySceneViewer): boolean {
    const modules = sceneViewer?.moduleMeshArr ?? [];
    return modules.some((module) => Boolean(module?.userData?.isBallastedRackType));
}

export function getInstallationTypes(
    sceneViewer: EligibilitySceneViewer,
    detachedStructureBuildingIds: number[] = [],
): { keys: string[]; label: string; presence: Record<string, boolean | null> } {
    const presence: Record<string, boolean | null> = Object.fromEntries(
        INSTALLATION_TYPE_ROWS.map((row) => [row.key, null]),
    );
    const faces = ((sceneViewer?.roofs?.[0]?.faces ?? []) as EligibilityFace[]);
    const detachedIds = new Set(detachedStructureBuildingIds.map(Number));

    if (faces.length === 0) {
        return { keys: [], label: '', presence };
    }

    presence.roofMount = false;
    presence.flatRoofTiltKit = false;
    presence.groundMount = false;
    presence.carport = false;
    presence.detachedStructure = false;

    faces.forEach((face) => {
        if (face?.surfaceMount) presence.groundMount = true;
        if (face?.carport === true) presence.carport = true;
        if (face?.flatRoof || face?.plane) {
            if (!face?.surfaceMount && face?.carport !== true) presence.flatRoofTiltKit = true;
        } else if (!face?.surfaceMount && face?.carport !== true) {
            presence.roofMount = true;
        }
        if (detachedIds.has(Number(face?.buildingName))) presence.detachedStructure = true;
    });

    const keys = INSTALLATION_TYPE_ROWS.map((row) => row.key).filter((key) => presence[key] === true);
    return {
        keys,
        label: keys.map((key) => INSTALLATION_LABELS[key] ?? key).join(', '),
        presence,
    };
}

/** Same formula as TotalSystemEfficiency Actual/Utility Offset. */
export function formatOffsetPercent(wattage: number, annualUsageKwh: number | null | undefined): string {
    if (annualUsageKwh == null || !Number.isFinite(annualUsageKwh) || annualUsageKwh <= 0) return '';
    if (!Number.isFinite(wattage)) return '';
    return `${Math.round((wattage / annualUsageKwh) * 100)}%`;
}

/**
 * Scene-derived eligibility reads that can be shipped to another window (pre-contract
 * embeds oneDRAW in an iframe). Excludes usage / battery, which the host owns.
 */
export type FinanceEligibilitySceneValues = Pick<
    EligibilityCurrentValues,
    | 'multiplier'
    | 'totalSystemSize'
    | 'moduleCount'
    | 'tiltRange'
    | 'tiltMin'
    | 'tiltMax'
    | 'tiltRoofs'
    | 'minPanelsPerArray'
    | 'smallestArrayCount'
    | 'hasBallastedRacking'
    | 'installationLabel'
    | 'installationKeys'
    | 'installationPresence'
    | 'actualOffsetPercent'
    | 'utilityOffsetPercent'
>;

export function pickFinanceEligibilitySceneValues(
    values: EligibilityCurrentValues,
): FinanceEligibilitySceneValues {
    return {
        multiplier: values.multiplier,
        totalSystemSize: values.totalSystemSize,
        moduleCount: values.moduleCount,
        tiltRange: values.tiltRange,
        tiltMin: values.tiltMin,
        tiltMax: values.tiltMax,
        tiltRoofs: values.tiltRoofs,
        minPanelsPerArray: values.minPanelsPerArray,
        smallestArrayCount: values.smallestArrayCount,
        hasBallastedRacking: values.hasBallastedRacking,
        installationLabel: values.installationLabel,
        installationKeys: values.installationKeys,
        installationPresence: values.installationPresence,
        actualOffsetPercent: values.actualOffsetPercent,
        utilityOffsetPercent: values.utilityOffsetPercent,
    };
}

export function collectFinanceEligibilityCurrentValues(input: {
    sceneViewer: EligibilitySceneViewer;
    roofInfoArr: RoofProductionInformation[] | undefined;
    roofPitchOverrideArr: RoofPitchData[] | undefined;
    multiplier: number;
    totalSystemSize: number;
    actualWattage: number;
    utilityWattage: number;
    annualUsageKwh: number | null;
    moduleCount: number;
    hasBattery: boolean;
    detachedStructureBuildingIds?: number[];
    propertyType?: string;
    ownershipType?: string;
    hasMetalShingle?: boolean | null;
    offsetsMultipleMeters?: boolean | null;
    isEnergyCommunity?: boolean;
}): EligibilityCurrentValues {
    const stateCode = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('stateCode')?.trim() ?? '' : '';
    const tilt = getEligibilityTiltExtents(
        input.sceneViewer,
        input.roofInfoArr,
        input.roofPitchOverrideArr,
    );
    const installation = getInstallationTypes(input.sceneViewer, input.detachedStructureBuildingIds);
    const smallestArrayCount = getSmallestArrayCount(input.roofInfoArr);
    return {
        stateCode,
        multiplier: input.multiplier,
        totalSystemSize: input.totalSystemSize,
        tiltRange: tilt.range,
        tiltMin: tilt.min,
        tiltMax: tilt.max,
        tiltRoofs: tilt.roofs,
        minPanelsPerArray: smallestArrayCount == null ? '' : String(smallestArrayCount),
        smallestArrayCount,
        moduleCount: Number.isFinite(input.moduleCount) ? input.moduleCount : 0,
        hasBallastedRacking: designHasBallastedRacking(input.sceneViewer),
        actualOffsetPercent: formatOffsetPercent(input.actualWattage, input.annualUsageKwh),
        utilityOffsetPercent: formatOffsetPercent(input.utilityWattage, input.annualUsageKwh),
        hasBattery: input.hasBattery,
        propertyType: input.propertyType?.trim() ?? '',
        ownershipType: input.ownershipType?.trim() ?? '',
        installationLabel: installation.label,
        installationKeys: installation.keys,
        installationPresence: installation.presence,
        hasMetalShingle: input.hasMetalShingle ?? null,
        offsetsMultipleMeters: input.offsetsMultipleMeters ?? null,
        isEnergyCommunity: Boolean(input.isEnergyCommunity),
    };
}

function opportunityHasBattery(
    opportunityDetails: any,
    productType?: string,
    financeProductType?: string,
): boolean {
    const codes = `${productType ?? ''}${financeProductType ?? ''}`.toUpperCase();
    if (codes.includes('B')) return true;
    const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest;
    if (interest?.Battery || interest?.BatteryOnly) return true;
    const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    return Array.isArray(products)
        && products.some((product: any) => /battery/i.test(String(product?.Product_Type_Name ?? product?.product_Type_Name ?? '')));
}

export type FailedSystemSpecFinalizeIssue = {
    label: string;
    reason: string;
};

export async function getFailedSystemSpecFinalizeIssues(options?: {
    useOneButtonAnnualInput?: boolean;
}): Promise<{ partnerName: string; issues: FailedSystemSpecFinalizeIssue[] } | null> {
    try {
        const [catalog, propertyDetails] = await Promise.all([
            fetchFinanceEligibilityCriteria(),
            fetchFinanceEligibilityPropertyDetails(),
        ]);
        const viewer = useViewerStore.getState() as any;
        const oneButton = useOneButtonStore.getState() as any;
        const partnerName =
            String(viewer.financePartnerDetails?.name ?? '').trim() ||
            getOpportunityPrimaryFinancePartner(oneButton.opportunityDetails).trim();
        const partner = findFinanceEligibilityPartner(partnerName, catalog);
        if (!partner || !isFinanceEligibilityValuePartner(partner)) return null;

        const su = oneButton.solarUtilityData?.SolarUtility as Record<string, unknown> | undefined;
        const annualUsageKwh = resolveEffectiveAnnualUsageKwh({
            useOneButtonAnnualInput: Boolean(options?.useOneButtonAnnualInput),
            oneButtonAnnualUsageOverride: viewer.annualUsage,
            solarUtility: su,
            sfAnnualUsage: viewer.SFannualUsage,
        });
        const values = collectFinanceEligibilityCurrentValues({
            sceneViewer: viewer.sceneViewer || {},
            roofInfoArr: viewer.roofInfoArr,
            roofPitchOverrideArr: viewer.roofPitchOverrideArr,
            multiplier: Number(viewer.multiplier) || 0,
            totalSystemSize: Number(viewer.totalSystemSize) || 0,
            actualWattage: Number(viewer.actualWattage) || 0,
            utilityWattage: Number(viewer.utilityWattage) || 0,
            annualUsageKwh,
            moduleCount: Number(viewer.modeCount) || 0,
            hasBattery: opportunityHasBattery(
                oneButton.opportunityDetails,
                viewer.productType,
                viewer.financePartnerDetails?.productType,
            ),
            detachedStructureBuildingIds: viewer.detachedStructureBuildingIds,
            propertyType: propertyDetails.propertyType,
            ownershipType: propertyDetails.ownershipType,
        });
        const statuses = evaluateFinanceEligibility(partnerName, values, catalog);
        const issues = getFailedSystemSpecReasons(partner, values, statuses).map((row) => ({
            label: row.label,
            reason: row.reason,
        }));
        if (!issues.length) return null;
        return { partnerName: partner.displayName || partnerName, issues };
    } catch {
        return null;
    }
}
